import { Form, Row, FormInstance, Col, Input } from 'antd';
import { useIntl } from 'react-intl';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { Purchase } from '@/core/domain/purchase/Purchase.types';
import {
  PurchaseItem,
  PurchaseItemCodeFormName,
  PurchaseItemsFormValues,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import NcmSelectedField from '@/components/_common/NcmSelectField';
import CfopSelectField from '@/components/_common/CfopSelectField';
import { useDefaultEmptyFieldError } from '@/hooks';
import Authorities from '@/types/Authority';
import { RequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';
import { quantityFormatter } from '@/utils/number';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { RegistrationsDefaultCodeLabel } from '@/types/Register';

interface Props {
  form: FormInstance<PurchaseItemsFormValues>;
  purchase?: Purchase;
  requestPurchase?: RequestPurchases;
  purchaseItem?: PurchaseItem;
}

function CreateItemPurchaseForm({
  form,
  purchase,
  requestPurchase,
  purchaseItem,
}: Props) {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const intl = useIntl();

  const handleValuesChanged = (
    fieldsChanged: PurchaseItemsFormValues,
    fieldValues: PurchaseItemsFormValues,
  ) => {
    if (fieldsChanged.quantity || fieldsChanged.unitaryValue) {
      form.resetFields(['discount']);
    }

    form.setFieldsValue({
      totalAmount:
        (fieldValues.quantity ?? 0) * (fieldValues.unitaryValue ?? 0) -
        (fieldValues.discount ?? 0),
    });
  };

  const quantity = Form.useWatch(['quantity'], form);

  const unitaryValue = Form.useWatch(['unitaryValue'], form);

  const { data: matrix } = useMatrix();

  const defaultPartCode = matrix?.matrixSettings.defaultPartCode;

  const purchaseReadOnly = !!purchase?.boschMigration;

  const isProPartsPurchase = !!purchase?.proPartsComplementId;

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleValuesChanged}
        disabled={purchaseReadOnly}
      >
        <Row gutter={[16, 9]}>
          {defaultPartCode && (
            <Col span={24} lg={6}>
              <FloatingFormItem
                name={PurchaseItemCodeFormName[defaultPartCode]}
                label={
                  defaultPartCode
                    ? RegistrationsDefaultCodeLabel[defaultPartCode]
                    : 'Código'
                }
              >
                <Input disabled />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} lg={18}>
            <FloatingFormItem
              name="name"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.description',
              })}
              rules={[defaultEmptyFieldError]}
            >
              <Input disabled />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={12}>
            <FloatingFormItem name="manufacturerId" label="Fabricante">
              <ManufacturerInfiniteSelectField queryFilter={{ active: true }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={12}>
            <FloatingFormItem
              name="ncmId"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.ncm',
              })}
            >
              <NcmSelectedField
                queryFilter={{ active: true }}
                disabled={
                  !hasPermissions([Authorities.REGISTER_NCM_VIEW]) ||
                  purchaseReadOnly
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={20}>
            <FloatingFormItem
              name="cfopId"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.cfop',
              })}
            >
              <CfopSelectField queryFilter={{ output: true }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="unitMeasurement"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.unit',
              })}
              rules={[defaultEmptyFieldError]}
            >
              <Input maxLength={5} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="quantity"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.quantity',
              })}
              initialValue={0}
              rules={
                requestPurchase && quantity >= (purchaseItem?.quantity ?? 0)
                  ? [
                      defaultEmptyFieldError,
                      {
                        type: 'number',
                        min: purchaseItem?.amountReturned ?? 0.01,
                        message: purchaseItem?.amountReturned
                          ? `O mínimo que poderá adicionar é ${purchaseItem?.amountReturned}`
                          : 'Infome uma quantidade maior que 0!',
                      },
                      {
                        type: 'number',
                        max:
                          (requestPurchase?.requestedQuantity ?? 0) -
                          ((requestPurchase?.quantityPurchased ?? 0) +
                            (requestPurchase.quantitySentPurchased ?? 0) -
                            (purchaseItem?.quantity ?? 0)),
                        message: `O máximo que poderá adicionar é ${quantityFormatter(
                          (requestPurchase?.requestedQuantity ?? 0) -
                            ((requestPurchase?.quantityPurchased ?? 0) +
                              (requestPurchase.quantitySentPurchased ?? 0) -
                              (purchaseItem?.quantity ?? 0)),
                        )}`,
                      },
                    ]
                  : [
                      defaultEmptyFieldError,
                      {
                        type: 'number',
                        min: purchaseItem?.amountReturned ?? 0.01,
                        message: purchaseItem?.amountReturned
                          ? `O mínimo que poderá adicionar é ${purchaseItem?.amountReturned}`
                          : 'Infome uma quantidade maior que 0!',
                      },
                    ]
              }
            >
              <InputDecimal
                disabled={
                  (purchaseItem?.amountReturned || 0) > 0 ||
                  purchaseReadOnly ||
                  isProPartsPurchase
                }
                min={0}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="unitaryValue"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.unitPrice',
              })}
              initialValue={0}
              rules={[defaultEmptyFieldError]}
            >
              <InputDecimal
                fractions={4}
                min={0}
                disabled={isProPartsPurchase}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="discount"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.discountValue',
              })}
            >
              <InputDecimal
                min={0}
                max={(quantity ?? 0) * (unitaryValue ?? 0)}
                disabled={isProPartsPurchase}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="totalAmount"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.amount',
              })}
              initialValue={0}
            >
              <InputDecimal disabled />
            </FloatingFormItem>
          </Col>

          {purchase?.individuallyExpenses && (
            <Col span={24} lg={4}>
              <FloatingFormItem
                name={['purchaseItemTaxes', 'expenseValue']}
                label={intl.formatMessage({
                  id: 'purchases.details.itemsPurchase.create.expenses',
                })}
                initialValue={0}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="itemNumber"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.itemNumber',
              })}
            >
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default CreateItemPurchaseForm;
